<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-card-title class="pb-0">
          <h1 class="headline font-weight-medium mb-2 mx-1">
            {{ isNew ? "Створення оплати" : "Оплата" }}
          </h1>
          <v-spacer />
          <ActionsModalWindow
            v-if="modal"
            :id="id"
            component-name="Payment"
            @close="$emit('close')"
          />
        </v-card-title>
        <v-form>
          <v-card-text class="py-0">
            <v-row>
              <v-col
                md="3"
                sm="6"
                cols="12"
              >
                <DatePicker
                  v-model="date"
                  default-value
                  :filled="false"
                  label="Дата*"
                  :error-messages="$v.date.$error ? errors.DATE : []"
                  :hide-details="false"
                  @input="$v.date.$touch()"
                />
              </v-col>

              <v-col
                md="9"
                sm="12"
              >
                <InvoiceSelect
                  v-model="invoice"
                  icon="search"
                  :invoice-id="invoice ? invoice.id : null"
                  :client-id="client ? client.id : null"
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="mt-n5 pt-0"
              >
                <v-radio-group
                  v-model="rolePaymentType"
                  row
                  label="Тип Оплати"
                  class="mb-n6"
                >
                  <v-radio
                    :value="paymentTypes.BANK.value"
                    :label="paymentTypes.BANK.title"
                    color="green"
                    @change="isChanged = true"
                  />
                  <v-radio
                    :value="paymentTypes.CARD.value"
                    :label="paymentTypes.CARD.title"
                    color="green"
                    @change="isChanged = true"
                  />
                  <v-radio
                    :value="paymentTypes.CASH.value"
                    :label="paymentTypes.CASH.title"
                    color="green"
                    @change="isChanged = true"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                md="6"
                sm="12"
                class="mb-n4"
              >
                <ClientSelect
                  v-model="client"
                  :client-id="client && client.id"
                  icon="search"
                  label="Клієнт*"
                  :error-messages="$v.client.title.$error ? errors.CLIENT : []"
                  :hide-details="false"
                  @input="$v.client.$touch()"
                  @change="changeClient"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                sm="4"
                class="pt-0"
              >
                <DecimalEditor
                  v-model="hours"
                  label="Години"
                  :scale="2"
                  @input="isChanged = true"
                />
              </v-col>

              <v-col
                cols="12"
                sm="4"
                class="pt-0"
              >
                <DecimalEditor
                  v-model="amount"
                  required
                  :scale="2"
                  label="Сума"
                  :error-messages="$v.amount.$error ? amountErrors : []"
                  :hide-details="false"
                  @input="$v.amount.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                sm="4"
                class="pt-0"
              >
                <v-switch
                  v-model="isPaymentForPackage"
                  label="Сплата Пакету"
                  color="green"
                  @change="isChanged = true"
                />
                <v-switch
                  v-model="balanceCorrection"
                  label="Введення залишків"
                  color="green"
                  class="mb-n7"
                  @change="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                sm="6"
                cols="12"
              >
                <PaymentSourceSelect
                  v-model="paymentSource"
                  label="Джерело Коштів"
                  @input="isChanged = true"
                />
              </v-col>
              <v-col
                sm="6"
                cols="12"
              >
                <PayerSelect
                  v-model="payer"
                  label="Платник"
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-textarea
                  v-model="paymentPurpose"
                  label="Призначення Платежу"
                  outlined
                  dense
                  placeholder="Призначення Платежу"
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <h2>Повідомлення</h2>
                <v-data-table
                  :headers="notificationsHeaders"
                  :items="notifications"
                  :hide-default-footer="notifications.length < 11"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions v-if="!modal">
            <PrimaryBtn
              :has-warnings="$v.$invalid"
              :loading="loading"
              @click="save(false)"
            />
            <PrimaryBtn
              :has-warnings="$v.$invalid"
              :loading="loading"
              text="Зберегти та повідомити"
              @click="save(true)"
            />
            <v-btn @click="$router.go(-1)">
              Відмінити
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import ClientSelect from "@/components/selects/ClientSelect";
import InvoiceSelect from "../invoices/InvoiceSelect";
import DecimalEditor from "@/components/DecimalEditor";
import PaymentSourceSelect from "../../catalogs/payment-sources/PaymentSourceSelect";
import DatePicker from "@/components/story/DatePicker";
import * as paymentsApi from "../../../backend/paymentsApi.js";
import PayerSelect from "../../catalogs/payers/PayerSelect.vue";
import paymentTypes from "../../../helpers/paymentTypes.js";
import PrimaryBtn from "@/components/PrimaryBtn.vue";

import { ERRORS } from "@/helpers";
import { required } from "vuelidate/lib/validators";

export default {
  components: {
    ClientSelect,
    DecimalEditor,
    InvoiceSelect,
    PaymentSourceSelect,
    DatePicker,
    PayerSelect,
    PrimaryBtn,
    ActionsModalWindow() {
      return import("@/components/ActionsModalWindow.vue");
    },
  },

  props: ["id", "modal"],

  data: () => ({
    dateMenu: false,
    date: null,
    client: null,
    hours: 0,
    amount: 0,
    rolePaymentType: null,
    paymentSource: null,
    invoice: null,
    paymentPurpose: null,
    loading: false,
    isChanged: false,
    balanceCorrection: false,
    isPaymentForPackage: false,
    payer: null,
    notifications: [],
    notificationsHeaders: [
      {
        text: "Від",
        sortable: false,
        value: "deliveredAt",
      },
      {
        text: "Email",
        sortable: false,
        value: "email",
      },
      {
        text: "Доставлено",
        sortable: false,
        value: "isDilevered",
      },
    ],
  }),

  validations: {
    client: {
      id: { required },
      title: { required },
    },
    date: {
      required,
    },
    amount: {
      required(value) {
        if (!this.balanceCorrection){
          return value > 0;
        } else {
          return value > 0 || value < 0;
        }
      },
    },
  },

  computed: {
    isNew() {
      return +this.id ? false : true;
    },
    formattedDate() {
      return this.date ? this.moment(this.date).format("DD.MM.YYYY") : "";
    },
    clientId() {
      return this.client ? this.client.id : null;
    },
    paymentTypes(){
      return paymentTypes;
    },
    errors() {
      return {
        CLIENT: [ERRORS.REQUIRED],
        DATE: [ERRORS.REQUIRED],
        AMOUNT: [ERRORS.REQUIRED],
        AMOUNT_AND_BALANCE_CORRECTION: [ERRORS.AMOUNT_AND_BALANCE_CORRECTION],
      };
    },
    amountErrors(){
      if (!this.amount){
        return this.errors.AMOUNT;
      } else {
        return this.errors.AMOUNT_AND_BALANCE_CORRECTION;
      }
    },
  },
  watch: {
    rolePaymentType: {
      handler(val){
        if (this.isNew && val === null) {
          this.rolePaymentType = this.paymentTypes.BANK.value;
        }
      },
      immediate: true,
    },
  },
  async created() {
    if (+this.id) {
      paymentsApi.getPayment(this.id).then((payment) => {
        this.date = payment.date;
        this.amount = payment.amount;
        this.balanceCorrection = payment.balanceCorrection;
        this.isPaymentForPackage = payment.isPaymentForPackage;
        this.hours = payment.hours;
        this.client = payment.client;
        if (payment.invoice) {
          this.invoice = payment.invoice;
        }
        this.rolePaymentType = payment.paymentType.id;
        if (payment.paymentSource) {
          this.paymentSource = payment.paymentSource;
        }
        this.paymentPurpose = payment.paymentPurpose;
        this.payer = payment.payer;
      });
    }
    if (+this.id) {
      this.notifications = await paymentsApi.getNotifications(this.id);
    }
  },
  methods: {
    async save(notify = false) {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }
      this.loading = true;

      const payment = {
        clientId: this.client.id,
        date: this.date,
        amount: this.amount,
        isPaymentForPackage: this.isPaymentForPackage,
        paymentType: this.rolePaymentType,
        invoiceId: this.invoice ? this.invoice.id : null,
        hours: this.hours ? this.hours : 0,
        paymentSourceId: this.paymentSource ? this.paymentSource.id : null,
        paymentPurpose: this.paymentPurpose,
        balanceCorrection: this.balanceCorrection,
        payerId: this.payer ? this.payer.id : null,
      };

      try {
        if (!this.isNew) {
          await paymentsApi
            .updatePayment(this.id, payment, notify);
        } else {
          await paymentsApi
            .createPayment(payment, notify);
        }
        this.$notifySuccess();
        this.$router.go(-1);
      } catch {
        this.$notifyWarning();
      } finally {
        this.loading = false;
        this.isChanged = false;
      }
    },
    onDateChange() {
      this.dateMenu = false;
      this.isChanged = true;
    },
    changeClient() {
      this.isChanged = true;
    },
  },
};
</script>

